import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback } from "react";
import { Button, Form, FormField, Input, Textarea, Select } from "@project-mgmt/ui";
import { VENTURE_CATEGORIES } from "../model/constants";
import { VentureCategoryBadge } from "./VentureCategoryBadge";
import { VentureFundingProgress } from "./VentureFundingProgress";
import { VentureFileUpload } from "./VentureFileUpload";
import { VentureFileList } from "./VentureFileList";
import { useVentureFiles } from "../api/ventureFiles";
/**
 * Multi-step form for creating and editing ventures
 *
 * Requirements:
 * - 1.1, 1.2: Venture creation with required fields
 * - 1.3: Venture editing
 * - 1.4: Input validation
 * - 1.5: End date validation
 * - 3.1: Form validation with zod schemas
 * - 3.2: Edit mode restrictions
 * - 5.1, 5.2, 5.3: File upload integration
 *
 * Features:
 * - Multi-step form (Basic Info → Pitch → Media → Review)
 * - Real-time validation with zod schemas
 * - Draft save functionality
 * - Preview before submission
 * - Create and edit modes
 * - Mobile-friendly with large touch targets
 * - Accessible with proper ARIA labels
 */
export const VentureForm = ({ workspaceId, venture, onSubmit, onSaveDraft, onPublish, onCancel, isLoading = false, className = "", }) => {
    const isEditMode = !!venture;
    const isLiveVenture = venture?.status === "live";
    const isDraftVenture = venture?.status === "draft";
    // Form state
    const [currentStep, setCurrentStep] = useState("basic");
    const [formData, setFormData] = useState({
        name: venture?.name || "",
        type: venture?.type || "",
        description: venture?.description || "",
        pitchContent: venture?.pitchContent || "",
        targetGoal: venture?.targetGoal?.toString() || "",
        endDate: venture?.endDate ? venture.endDate.split("T")[0] : "",
    });
    const [errors, setErrors] = useState({});
    const [uploadError, setUploadError] = useState(null);
    // Track the draft venture created during the form flow
    const [draftVenture, setDraftVenture] = useState(venture);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    // Use either the existing venture or the newly created draft
    const activeVenture = draftVenture || venture;
    // Fetch existing files when we have a venture ID
    const { data: filesData, refetch: refreshFiles } = useVentureFiles(activeVenture?.id || null, activeVenture ? workspaceId : null);
    const existingFiles = filesData?.files || [];
    // Steps configuration
    const steps = [
        { id: "basic", label: "Basic Info" },
        { id: "pitch", label: "Pitch" },
        { id: "media", label: "Media", disabled: isLiveVenture },
        { id: "review", label: "Review" },
    ];
    const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
    /**
     * Update form field value
     */
    const updateField = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);
    /**
     * Validate current step
     */
    const validateStep = useCallback((step) => {
        const newErrors = {};
        if (step === "basic") {
            if (!formData.name.trim()) {
                newErrors.name = "Name is required";
            }
            else if (formData.name.length < 3) {
                newErrors.name = "Name must be at least 3 characters";
            }
            else if (formData.name.length > 200) {
                newErrors.name = "Name must be less than 200 characters";
            }
            if (!formData.type) {
                newErrors.type = "Category is required";
            }
            if (formData.description && formData.description.length > 500) {
                newErrors.description = "Description must be less than 500 characters";
            }
            if (!formData.targetGoal) {
                newErrors.targetGoal = "Funding goal is required";
            }
            else {
                const goal = parseFloat(formData.targetGoal);
                if (isNaN(goal) || goal < 100) {
                    newErrors.targetGoal = "Funding goal must be at least 100";
                }
            }
            if (formData.endDate) {
                const endDate = new Date(formData.endDate);
                const now = new Date();
                if (endDate <= now) {
                    newErrors.endDate = "End date must be in the future";
                }
            }
        }
        if (step === "pitch") {
            if (!formData.pitchContent.trim()) {
                newErrors.pitchContent = "Pitch content is required";
            }
            else if (formData.pitchContent.length < 100) {
                newErrors.pitchContent = "Pitch content must be at least 100 characters";
            }
            else if (formData.pitchContent.length > 10000) {
                newErrors.pitchContent = "Pitch content must be less than 10,000 characters";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);
    /**
     * Prepare form data for submission
     */
    const prepareSubmitData = useCallback(() => {
        const baseData = {
            name: formData.name.trim(),
            description: formData.description.trim() || undefined,
            pitchContent: formData.pitchContent.trim(),
            targetGoal: parseFloat(formData.targetGoal),
            endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        };
        // If we have a draft venture or are in edit mode, prepare update data
        if (isEditMode || draftVenture) {
            // Edit mode or updating draft: include type and targetGoal (backend will enforce restrictions for live ventures)
            return {
                ...baseData,
                type: formData.type,
            };
        }
        else {
            // Create mode: include all required fields
            return {
                ...baseData,
                workspaceId,
                type: formData.type,
            };
        }
    }, [formData, workspaceId, isEditMode, draftVenture]);
    /**
     * Navigate to next step
     * If moving from pitch to media in create mode, save as draft first
     */
    const goToNextStep = useCallback(async (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (!validateStep(currentStep)) {
            return;
        }
        // If moving from pitch to media in create mode and no draft exists yet, save as draft first
        if (currentStep === "pitch" && !activeVenture && onSaveDraft) {
            try {
                setIsSavingDraft(true);
                const submitData = prepareSubmitData();
                const createdVenture = await onSaveDraft(submitData);
                setDraftVenture(createdVenture);
            }
            catch (error) {
                console.error("Failed to save draft:", error);
                alert("Failed to save draft. Please try again.");
                return;
            }
            finally {
                setIsSavingDraft(false);
            }
        }
        const nextIndex = currentStepIndex + 1;
        if (nextIndex < steps.length) {
            // Skip disabled steps
            let nextStep = steps[nextIndex];
            let checkIndex = nextIndex;
            while (nextStep.disabled && checkIndex < steps.length - 1) {
                checkIndex++;
                nextStep = steps[checkIndex];
            }
            setCurrentStep(nextStep.id);
        }
    }, [currentStep, currentStepIndex, steps, validateStep, activeVenture, onSaveDraft, prepareSubmitData]);
    /**
     * Navigate to previous step
     */
    const goToPreviousStep = useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        const prevIndex = currentStepIndex - 1;
        if (prevIndex >= 0) {
            // Skip disabled steps
            let prevStep = steps[prevIndex];
            let checkIndex = prevIndex;
            while (prevStep.disabled && checkIndex > 0) {
                checkIndex--;
                prevStep = steps[checkIndex];
            }
            setCurrentStep(prevStep.id);
        }
    }, [currentStepIndex, steps]);
    /**
     * Navigate to specific step
     */
    const goToStep = useCallback((step, e) => {
        e?.preventDefault();
        e?.stopPropagation();
        const stepConfig = steps.find((s) => s.id === step);
        if (stepConfig && !stepConfig.disabled) {
            setCurrentStep(step);
        }
    }, [steps]);
    /**
     * Handle form submission (for edit mode - saves changes without publishing)
     */
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        // Validate all steps
        const isBasicValid = validateStep("basic");
        const isPitchValid = validateStep("pitch");
        if (!isBasicValid || !isPitchValid) {
            // Go to first invalid step
            if (!isBasicValid) {
                setCurrentStep("basic");
            }
            else if (!isPitchValid) {
                setCurrentStep("pitch");
            }
            return;
        }
        const submitData = prepareSubmitData();
        await onSubmit(submitData);
    }, [validateStep, prepareSubmitData, onSubmit]);
    /**
     * Handle publish action (changes status to 'live')
     */
    const handlePublish = useCallback(async (e) => {
        e.preventDefault();
        // Validate all steps
        const isBasicValid = validateStep("basic");
        const isPitchValid = validateStep("pitch");
        if (!isBasicValid || !isPitchValid) {
            // Go to first invalid step
            if (!isBasicValid) {
                setCurrentStep("basic");
            }
            else if (!isPitchValid) {
                setCurrentStep("pitch");
            }
            return;
        }
        if (!onPublish)
            return;
        try {
            setIsPublishing(true);
            // If we have a draft venture, publish it directly
            if (activeVenture) {
                await onPublish(activeVenture.id);
            }
            else {
                // If no draft exists yet, save as draft first, then publish
                if (onSaveDraft) {
                    const submitData = prepareSubmitData();
                    const createdVenture = await onSaveDraft(submitData);
                    await onPublish(createdVenture.id);
                }
            }
        }
        finally {
            setIsPublishing(false);
        }
    }, [validateStep, activeVenture, onPublish, onSaveDraft, prepareSubmitData]);
    /**
     * Handle draft save
     */
    const handleSaveDraft = useCallback(async (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (!onSaveDraft)
            return;
        const submitData = prepareSubmitData();
        const savedVenture = await onSaveDraft(submitData);
        // Update draft venture if this was a create operation
        if (!draftVenture && !isEditMode) {
            setDraftVenture(savedVenture);
        }
    }, [prepareSubmitData, onSaveDraft, draftVenture, isEditMode]);
    /**
     * Handle file upload complete
     */
    const handleFileUploadComplete = useCallback((file) => {
        setUploadError(null);
        // Refresh file list
        refreshFiles();
    }, [refreshFiles]);
    /**
     * Handle file upload error
     */
    const handleFileUploadError = useCallback((error) => {
        setUploadError(error);
    }, []);
    /**
     * Handle file delete
     */
    const handleFileDelete = useCallback((fileId) => {
        // Refresh file list
        refreshFiles();
    }, [refreshFiles]);
    // Category options for select
    const categoryOptions = Object.entries(VENTURE_CATEGORIES).map(([value, label]) => ({
        value,
        label,
    }));
    return (_jsxs("div", { className: `mx-auto max-w-4xl ${className}`, children: [_jsx("div", { className: "mb-8", children: _jsx("div", { className: "mb-4 flex items-center justify-between", children: steps.map((step, index) => (_jsxs(React.Fragment, { children: [_jsxs("button", { type: "button", onClick: (e) => goToStep(step.id, e), disabled: step.disabled || isLoading, className: `flex-1 rounded-lg px-4 py-2 text-center transition-colors ${currentStep === step.id ? "bg-black text-white" : "bg-gray-100 text-gray-600"} ${step.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-200"} ${currentStep === step.id && !step.disabled ? "hover:bg-gray-800" : ""} `, "aria-current": currentStep === step.id ? "step" : undefined, "aria-disabled": step.disabled, children: [_jsx("div", { className: "text-sm font-medium", children: step.label }), _jsxs("div", { className: "mt-1 text-xs", children: ["Step ", index + 1] })] }), index < steps.length - 1 && _jsx("div", { className: "mx-2 h-0.5 w-8 bg-gray-200", "aria-hidden": "true" })] }, step.id))) }) }), _jsxs(Form, { onSubmit: handleSubmit, className: "space-y-6", children: [currentStep === "basic" && (_jsxs("div", { className: "animate-fadeIn space-y-6", children: [_jsx("h2", { className: "mb-4 text-2xl font-bold text-black", children: "Basic Information" }), _jsxs(FormField, { label: "Venture Name", required: true, error: errors.name, children: [_jsx(Input, { type: "text", value: formData.name, onChange: (e) => updateField("name", e.target.value), placeholder: "Enter a compelling name for your venture", error: !!errors.name, disabled: isLoading || (isEditMode && isLiveVenture), maxLength: 200, "aria-describedby": errors.name ? "name-error" : undefined }), _jsxs("div", { className: "mt-1 text-xs text-gray-500", children: [formData.name.length, "/200 characters"] })] }), _jsx(FormField, { label: "Category", required: true, error: errors.type, children: _jsx(Select, { value: formData.type, onChange: (e) => {
                                        e.stopPropagation();
                                        updateField("type", e.target.value);
                                    }, options: categoryOptions, placeholder: "Select a category", error: !!errors.type, disabled: isLoading || (isEditMode && isLiveVenture), "aria-describedby": errors.type ? "type-error" : undefined }) }), formData.type && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm text-gray-600", children: "Preview:" }), _jsx(VentureCategoryBadge, { category: formData.type })] })), _jsxs(FormField, { label: "Short Description", error: errors.description, children: [_jsx(Textarea, { value: formData.description, onChange: (e) => updateField("description", e.target.value), placeholder: "Brief description (optional, max 500 characters)", error: !!errors.description, disabled: isLoading, rows: 3, maxLength: 500, "aria-describedby": errors.description ? "description-error" : undefined }), _jsxs("div", { className: "mt-1 text-xs text-gray-500", children: [formData.description.length, "/500 characters"] })] }), _jsxs(FormField, { label: "Funding Goal", required: true, error: errors.targetGoal, children: [_jsxs("div", { className: "relative", children: [_jsx("span", { className: "absolute left-3 top-2 text-gray-500", children: "$" }), _jsx(Input, { type: "number", value: formData.targetGoal, onChange: (e) => updateField("targetGoal", e.target.value), placeholder: "0.00", error: !!errors.targetGoal, disabled: isLoading || (isEditMode && isLiveVenture), min: "100", step: "0.01", className: "pl-8", "aria-describedby": errors.targetGoal ? "targetGoal-error" : undefined })] }), _jsx("div", { className: "mt-1 text-xs text-gray-500", children: "Minimum funding goal is $100" })] }), formData.targetGoal && parseFloat(formData.targetGoal) >= 100 && (_jsxs("div", { className: "rounded-lg bg-gray-50 p-4", children: [_jsx("div", { className: "mb-2 text-sm font-medium text-gray-700", children: "Funding Goal Preview" }), _jsx(VentureFundingProgress, { fundsPledged: 0, targetGoal: parseFloat(formData.targetGoal), showAmounts: true })] })), _jsxs(FormField, { label: "Campaign End Date", error: errors.endDate, children: [_jsx(Input, { type: "date", value: formData.endDate, onChange: (e) => updateField("endDate", e.target.value), error: !!errors.endDate, disabled: isLoading, min: new Date().toISOString().split("T")[0], "aria-describedby": errors.endDate ? "endDate-error" : undefined }), _jsx("div", { className: "mt-1 text-xs text-gray-500", children: "Optional: Set a deadline for your funding campaign" })] })] })), currentStep === "pitch" && (_jsxs("div", { className: "animate-fadeIn space-y-6", children: [_jsx("h2", { className: "mb-4 text-2xl font-bold text-black", children: "Your Pitch" }), _jsxs(FormField, { label: "Pitch Content", required: true, error: errors.pitchContent, children: [_jsx(Textarea, { value: formData.pitchContent, onChange: (e) => updateField("pitchContent", e.target.value), placeholder: "Describe your business opportunity in detail. What problem does it solve? What makes it unique? Why should people back it?", error: !!errors.pitchContent, disabled: isLoading, rows: 15, maxLength: 10000, "aria-describedby": errors.pitchContent ? "pitchContent-error" : undefined }), _jsxs("div", { className: "mt-1 text-xs text-gray-500", children: [formData.pitchContent.length, "/10,000 characters (minimum 100)"] })] }), _jsxs("div", { className: "rounded-lg border border-blue-200 bg-blue-50 p-4", children: [_jsx("h3", { className: "mb-2 text-sm font-semibold text-blue-900", children: "\uD83D\uDCA1 Tips for a great pitch:" }), _jsxs("ul", { className: "list-inside list-disc space-y-1 text-sm text-blue-800", children: [_jsx("li", { children: "Start with the problem you're solving" }), _jsx("li", { children: "Explain your solution and what makes it unique" }), _jsx("li", { children: "Describe your target market and opportunity size" }), _jsx("li", { children: "Share your business model and revenue strategy" }), _jsx("li", { children: "Highlight your team's expertise and experience" }), _jsx("li", { children: "Explain how you'll use the funds" })] })] })] })), currentStep === "media" && !isLiveVenture && (_jsxs("div", { className: "animate-fadeIn space-y-6", children: [_jsx("h2", { className: "mb-4 text-2xl font-bold text-black", children: "Media & Documents" }), _jsxs("div", { className: "space-y-4", children: [_jsx("p", { className: "text-sm text-gray-600", children: "Add images and documents to support your pitch. Visual content helps backers understand your venture better." }), uploadError && (_jsx("div", { className: "rounded-lg border border-red-200 bg-red-50 p-3", role: "alert", children: _jsx("p", { className: "text-sm text-red-800", children: uploadError }) })), activeVenture ? (_jsxs(_Fragment, { children: [_jsx(VentureFileUpload, { ventureId: activeVenture.id, workspaceId: workspaceId, onUploadComplete: handleFileUploadComplete, onUploadError: handleFileUploadError, maxFileSize: 10 * 1024 * 1024, multiple: true, disabled: isLoading }), existingFiles.length > 0 && (_jsxs("div", { className: "mt-6", children: [_jsx("h3", { className: "mb-3 text-sm font-medium text-gray-700", children: "Uploaded Files" }), _jsx(VentureFileList, { ventureId: activeVenture.id, workspaceId: workspaceId, files: existingFiles, canDelete: true, onFileDeleted: handleFileDelete })] }))] })) : (_jsx("div", { className: "rounded-lg border border-blue-200 bg-blue-50 p-6", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "mb-2 text-4xl", "aria-hidden": "true", children: "\u2139\uFE0F" }), _jsx("p", { className: "mb-1 text-sm font-medium text-blue-800", children: "Venture will be saved as draft" }), _jsx("p", { className: "text-xs text-blue-700", children: "When you click \"Next\", your venture will be saved as a draft so you can upload files." })] }) })), _jsxs("div", { className: "rounded-lg border border-gray-200 bg-gray-50 p-4", children: [_jsx("h3", { className: "mb-2 text-sm font-semibold text-gray-700", children: "\uD83D\uDCCB File Guidelines:" }), _jsxs("ul", { className: "list-inside list-disc space-y-1 text-sm text-gray-600", children: [_jsx("li", { children: "Supported formats: JPEG, PNG, GIF, WebP, PDF" }), _jsx("li", { children: "Maximum file size: 10MB per file" }), _jsx("li", { children: "Use high-quality images to showcase your venture" }), _jsx("li", { children: "Include diagrams, mockups, or business plans as PDFs" }), _jsx("li", { children: "Ensure all content is relevant and professional" })] })] })] })] })), currentStep === "review" && (_jsxs("div", { className: "animate-fadeIn space-y-6", children: [_jsx("h2", { className: "mb-4 text-2xl font-bold text-black", children: "Review Your Venture" }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "rounded-lg bg-gray-50 p-4", children: [_jsx("h3", { className: "mb-3 text-sm font-semibold text-gray-700", children: "Basic Information" }), _jsxs("dl", { className: "space-y-2", children: [_jsxs("div", { children: [_jsx("dt", { className: "text-xs text-gray-500", children: "Name" }), _jsx("dd", { className: "text-sm font-medium text-black", children: formData.name })] }), _jsxs("div", { children: [_jsx("dt", { className: "text-xs text-gray-500", children: "Category" }), _jsx("dd", { className: "text-sm", children: formData.type && _jsx(VentureCategoryBadge, { category: formData.type }) })] }), formData.description && (_jsxs("div", { children: [_jsx("dt", { className: "text-xs text-gray-500", children: "Description" }), _jsx("dd", { className: "text-sm text-gray-700", children: formData.description })] })), _jsxs("div", { children: [_jsx("dt", { className: "text-xs text-gray-500", children: "Funding Goal" }), _jsxs("dd", { className: "text-sm font-medium text-black", children: ["$", parseFloat(formData.targetGoal).toLocaleString()] })] }), formData.endDate && (_jsxs("div", { children: [_jsx("dt", { className: "text-xs text-gray-500", children: "End Date" }), _jsx("dd", { className: "text-sm text-gray-700", children: new Date(formData.endDate).toLocaleDateString() })] }))] })] }), _jsxs("div", { className: "rounded-lg bg-gray-50 p-4", children: [_jsx("h3", { className: "mb-3 text-sm font-semibold text-gray-700", children: "Pitch Content" }), _jsx("div", { className: "max-h-64 overflow-y-auto whitespace-pre-wrap text-sm text-gray-700", children: formData.pitchContent })] }), existingFiles.length > 0 && (_jsxs("div", { className: "rounded-lg bg-gray-50 p-4", children: [_jsxs("h3", { className: "mb-3 text-sm font-semibold text-gray-700", children: ["Media (", existingFiles.length, " ", existingFiles.length === 1 ? "file" : "files", ")"] }), _jsx("div", { className: "space-y-1", children: existingFiles.map((file) => (_jsxs("div", { className: "flex items-center gap-2 text-sm text-gray-700", children: [_jsx("span", { "aria-hidden": "true", children: file.mimeType?.startsWith("image/") ? "🖼️" : "📄" }), _jsx("span", { className: "truncate", children: file.filename })] }, file.id))) })] }))] }), _jsx("div", { className: "rounded-lg border border-yellow-200 bg-yellow-50 p-4", children: _jsxs("p", { className: "text-sm text-yellow-800", children: [_jsx("strong", { children: "Note:" }), " ", isEditMode && isLiveVenture
                                            ? "Your changes will be saved. The venture will remain live."
                                            : isEditMode && isDraftVenture
                                                ? "You can save changes as a draft or publish to make it live."
                                                : activeVenture
                                                    ? "You can save changes as a draft or publish to make it live."
                                                    : "You can save as a draft or publish immediately to make it live."] }) })] })), _jsxs("div", { className: "flex items-center justify-between border-t pt-6", children: [_jsxs("div", { className: "flex gap-2", children: [currentStepIndex > 0 && (_jsx(Button, { type: "button", onClick: (e) => goToPreviousStep(e), variant: "outline", disabled: isLoading || isSavingDraft || isPublishing, children: "\u2190 Previous" })), onCancel && (_jsx(Button, { type: "button", onClick: (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onCancel();
                                        }, variant: "outline", disabled: isLoading || isSavingDraft || isPublishing, children: "Cancel" }))] }), _jsxs("div", { className: "flex gap-2", children: [onSaveDraft && currentStep !== "review" && !isLiveVenture && (_jsx(Button, { type: "button", onClick: (e) => handleSaveDraft(e), variant: "outline", disabled: isLoading || isSavingDraft || isPublishing, children: isSavingDraft ? "Saving..." : "Save Draft" })), currentStepIndex < steps.length - 1 ? (_jsx(Button, { type: "button", onClick: (e) => goToNextStep(e), disabled: isLoading || isSavingDraft || isPublishing, children: isSavingDraft ? "Saving Draft..." : "Next →" })) : (
                                    // Review step buttons
                                    _jsxs(_Fragment, { children: [onSaveDraft && !isLiveVenture && (_jsx(Button, { type: "button", onClick: (e) => handleSaveDraft(e), variant: "outline", disabled: isLoading || isSavingDraft || isPublishing, children: isSavingDraft ? "Saving..." : "Save Draft" })), isLiveVenture ? (
                                            // For live ventures, only allow saving changes
                                            _jsx(Button, { type: "submit", disabled: isLoading || isSavingDraft || isPublishing, children: isLoading ? "Saving..." : "Save Changes" })) : onPublish ? (
                                            // For draft ventures or new ventures, show Publish button
                                            _jsx(Button, { type: "button", onClick: (e) => handlePublish(e), disabled: isLoading || isSavingDraft || isPublishing, children: isPublishing ? "Publishing..." : "Publish Venture" })) : (
                                            // Fallback if no publish handler provided
                                            _jsx(Button, { type: "submit", disabled: isLoading || isSavingDraft || isPublishing, children: isLoading ? "Submitting..." : isEditMode ? "Save Changes" : "Create Venture" }))] }))] })] })] })] }));
};
